import {
  checkIfHasHiddenVerificationController,
  createEvoClient,
  getAvailableVerificationMethodsController,
  initializeAsynchronousAgeVerificationController,
  performAgeVerificationController,
} from "@jti/evo-client";
import type {
  AvailableMethodsResponse,
  InitAsyncVerificationPayload,
  InitAsyncVerificationResponse,
  VerifyResponse,
  VerifyVariables,
} from "@jti/fe-common";
import { DOB_INPUT_DATE, ISO_DATE_FORMAT, parseCustomDate } from "@jti/fe-common";
import type { AvpContract } from "@jti/rest-api-types";

import { getUser } from "../../../utils";

import { getEvoVerificationStatus, getIsEvoVerificationPerformed } from "./utils";

export class AvpApiEvo implements AvpContract {
  private readonly client;
  constructor(
    baseUrl: string,
    tenantToken: string,
    protected recaptchaId: string,
    private isoCountryCode: string
  ) {
    const { ciam_token } = getUser();
    this.client = createEvoClient({
      baseUrl,
      throwOnError: true,
      recaptchaId,
      tenantToken,
      ciamToken: ciam_token,
    });
  }

  async getAvailableMethods(): Promise<AvailableMethodsResponse> {
    const { data, response } = await getAvailableVerificationMethodsController({
      client: this.client,
    });

    if (!data?.data) {
      throw new Error("EVO is missing the data field in the response related to the available methods");
    }

    return {
      data: data.data,
      statusCode: response.status,
    } as AvailableMethodsResponse;
  }

  async isAgeVerificationHidden(): Promise<boolean> {
    const { data } = await checkIfHasHiddenVerificationController({
      client: this.client,
    });

    if (!data) {
      throw new Error("EVO is missing the data field in the response related to the hidden verification");
    }

    // TODO: Return the correct type of checkIfHasHiddenVerificationController response
    return Boolean((data as { data: { has_hidden_verification: boolean } }).data?.has_hidden_verification);
  }

  async verify(variables: VerifyVariables): Promise<VerifyResponse> {
    const { user_email, fields, ...rest } = variables;

    const requestBody = {
      ...rest,
      email: user_email,
      ...(fields && {
        fields: {
          ...fields,
          ...(fields.workflowName && { workflow_name: fields.workflowName }),
          ...(fields.workflowDefinitionId && { workflow_definition_id: fields.workflowDefinitionId }),
          user_email: fields.userEmail ?? user_email,
        },
      }),
    };

    const { data } = await performAgeVerificationController({
      client: this.client,
      body: requestBody,
    });

    const verificationData = data?.data;

    if (!verificationData) {
      throw new Error("EVO is missing the data field in the response related to the verification");
    }

    return {
      is_verified: Boolean(verificationData.is_verified),
      verification_performed: getIsEvoVerificationPerformed(verificationData.verification_status),
      uuid: verificationData.uuid ?? "",
      status_from_provider: verificationData.verification_status,
      consumer_data: verificationData.consumer_data,
      error: verificationData.error,
    } as VerifyResponse;
  }

  async initAsyncVerification({
    methodCode,
    fields,
    userEmail,
  }: InitAsyncVerificationPayload): Promise<InitAsyncVerificationResponse> {
    const requestBody = {
      email: userEmail,
      method_code: methodCode,
      country_code: this.isoCountryCode,
      ...(fields && {
        fields: {
          ...fields,
          ...(fields.dateOfBirth && {
            dateOfBirth: parseCustomDate({
              date: fields.dateOfBirth,
              initialFormat: DOB_INPUT_DATE,
              resultFormat: ISO_DATE_FORMAT,
            }),
          }),
          ...(fields.workflowName && { workflow_name: fields.workflowName }),
          ...(fields.workflowDefinitionId && { workflow_definition_id: fields.workflowDefinitionId }),
        },
      }),
    };

    const { data } = await initializeAsynchronousAgeVerificationController({
      client: this.client,
      body: requestBody,
    });

    const evoData = data?.data;

    if (!evoData) {
      throw new Error("EVO is missing the data field in the response related to the async verification");
    }

    return {
      uuid: evoData?.uuid ?? "",
      status: getEvoVerificationStatus(evoData?.verification_status),
      verification_performed: getIsEvoVerificationPerformed(evoData?.verification_status),
      data: evoData?.data ?? {},
    };
  }
}
